/**
 * ElderRevealPostProcess — full-screen shader pass + particle curtain
 * used during the Milestone 4.C Elder reveal cutscenes.
 *
 * Per companion book 2026-04-14, two Elders have canon reveal beats:
 *
 *   - Kitty (p. 14): "Following the Apex puzzle, the scrap falls away
 *     into a starry void. Kitty, a slight black ninja, appears at the
 *     far edge of that void."
 *   - Gus   (p. 19): "The feral greenhouse melts away into a quiet,
 *     plush parlor. At its center sits Gus, a distinguished tabby."
 *
 * Implementation shape:
 *   1. A dedicated full-screen PostProcess chains radial blur +
 *      chromatic aberration driven by a `strength` uniform in [0..1].
 *      A `preset` uniform selects between the two canon tonal mixes:
 *        preset 0 → starry-void  (cool indigo wash)
 *        preset 1 → parlor-dissolve (warm plum wash with chromatic noise)
 *   2. A particle emitter layer paints the preset-specific curtain:
 *        starry-void     — brown scrap rains down and dissolves mid-
 *                          frame into white/cyan stars on indigo void.
 *        parlor-dissolve — faint Victorian parlor silhouette (wingback
 *                          chair + oil lamp) over a plum wash, which
 *                          dissolves outward as chromatic aberration
 *                          ramps up revealing the Elder.
 *
 * Design parallels `VisionPostProcess` (Making Biscuits) but the two
 * serve very different beats (vision = desaturated memory; reveal =
 * environment dissolving into its truer nature), so they sit side-by-
 * side rather than sharing a base class. The only logic reused
 * verbatim is the "fail silently on NullEngine" attach pattern — see
 * the `try/catch` in the constructor. Both classes follow the same
 * `isAttached` / `animateStrength` / `dispose` surface so the
 * CutsceneSystem can treat them uniformly.
 *
 * Lifecycle is one-shot: `start(preset, durationMs, onComplete)` spins
 * up the shader + particles, ramps the uniform to full, holds, ramps
 * back to zero, and invokes the callback. `dispose()` is always safe
 * to call and tears down every allocated resource.
 */
import { Scene } from "@babylonjs/core/scene";
import { Camera } from "@babylonjs/core/Cameras/camera";
import { PostProcess } from "@babylonjs/core/PostProcesses/postProcess";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
// Ray import is a side-effect: `Camera.getForwardRay()` is wired onto
// the camera prototype only when the Ray module is loaded. Without it
// the parlor-dissolve preset throws in unit-test harnesses that tree-
// shake Babylon aggressively (see vitest failure in elder-reveal.spec).
import "@babylonjs/core/Culling/ray";

export type ElderRevealPreset = "starry-void" | "parlor-dissolve";

/**
 * Fragment shader. Mix controlled by `preset`:
 *   0.0 → starry-void (cool indigo wash + radial blur)
 *   1.0 → parlor-dissolve (warm plum wash + chromatic aberration)
 *
 * `strength` ramps both the wash and the distortion from 0 (scene
 * untouched) to 1 (full curtain). A soft vignette tightens the frame
 * so the Elder silhouette reads center-stage.
 */
const ELDER_REVEAL_FRAGMENT = `
varying vec2 vUV;
uniform sampler2D textureSampler;
uniform float strength;   // 0..1 — overall curtain opacity / distortion
uniform float preset;     // 0.0 = starry-void, 1.0 = parlor-dissolve
uniform float chromatic;  // max chromatic UV offset at full strength

void main(void) {
  vec2 centered = vUV - vec2(0.5, 0.5);
  float d = length(centered);

  // Chromatic aberration — slightly stronger for parlor-dissolve so the
  // plum wash dissolves outward rather than imploding.
  float aberration = mix(0.6, 1.0, preset) * chromatic * strength;
  vec2 dir = normalize(centered + vec2(0.00001));
  float r = texture2D(textureSampler, vUV + dir * aberration).r;
  float g = texture2D(textureSampler, vUV).g;
  float b = texture2D(textureSampler, vUV - dir * aberration).b;
  vec3 color = vec3(r, g, b);

  // Radial soft blur — approximate with two extra taps, blended by
  // strength. Good enough for a 2.5s reveal; a full Gaussian pass would
  // be overkill and expensive on low-end machines.
  vec3 blurA = texture2D(textureSampler, vUV + dir * 0.004 * strength).rgb;
  vec3 blurB = texture2D(textureSampler, vUV - dir * 0.004 * strength).rgb;
  vec3 blurred = (color + blurA + blurB) / 3.0;
  color = mix(color, blurred, strength * 0.6);

  // Wash tint — cool indigo for starry-void, warm plum for parlor.
  // Kept below 0.5 mix weight so the scene never goes fully matte.
  vec3 voidTint   = vec3(0.05, 0.06, 0.14);   // deep indigo
  vec3 parlorTint = vec3(0.18, 0.08, 0.12);   // warm plum
  vec3 tint = mix(voidTint, parlorTint, preset);
  color = mix(color, tint, strength * 0.45);

  // Vignette — darkens the edges so the curtain reads as a theatrical
  // reveal rather than a flat overlay.
  float vig = 1.0 - smoothstep(0.35, 0.95, d) * 0.55 * strength;
  gl_FragColor = vec4(color * vig, 1.0);
}
`;

if (!Effect.ShadersStore["elderRevealPostFragmentShader"]) {
  Effect.ShadersStore["elderRevealPostFragmentShader"] = ELDER_REVEAL_FRAGMENT;
}

/**
 * Builds the ash-and-rust scrap sprite used by the starry-void emitter.
 *
 * Gothic steampunk palette per CLAUDE.md — "brown scrap should look
 * like ash+rust, not debris." A radial gradient with warm iron core
 * and cool soot falloff reads as tumbling rust flakes under additive
 * blending.
 */
function makeScrapSprite(scene: Scene): Texture {
  const size = 64;
  const dt = new DynamicTexture("elderRevealScrapSprite", size, scene, false);
  const ctx = dt.getContext();
  const grad = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
  // Warm rust core → soot edge → transparent.
  grad.addColorStop(0, "rgba(176, 112, 64, 1)");
  grad.addColorStop(0.45, "rgba(110, 68, 40, 0.85)");
  grad.addColorStop(0.8, "rgba(52, 34, 22, 0.35)");
  grad.addColorStop(1, "rgba(24, 16, 10, 0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  dt.update(false);
  dt.hasAlpha = true;
  return dt;
}

/**
 * White/cyan star sprite — a tight radial gradient with a faint cyan
 * halo so pixels read as starlight rather than dust.
 */
function makeStarSprite(scene: Scene): Texture {
  const size = 48;
  const dt = new DynamicTexture("elderRevealStarSprite", size, scene, false);
  const ctx = dt.getContext();
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0.5, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(255, 255, 255, 1)");
  grad.addColorStop(0.4, "rgba(190, 230, 255, 0.9)");
  grad.addColorStop(1, "rgba(120, 180, 220, 0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  dt.update(false);
  dt.hasAlpha = true;
  return dt;
}

/**
 * Filled-oval helper — Babylon's ICanvasRenderingContext typedef omits
 * `ellipse`, and some headless canvas polyfills don't implement it,
 * so we emulate it with a transform + arc pair. Safe on every renderer
 * the project has ever supported.
 */
function drawOval(
  ctx: { save: () => void; translate: (x: number, y: number) => void; scale: (x: number, y: number) => void;
         beginPath: () => void; arc: (x: number, y: number, r: number, a: number, b: number) => void;
         fill: () => void; restore: () => void },
  cx: number, cy: number, rx: number, ry: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(rx, ry);
  ctx.beginPath();
  ctx.arc(0, 0, 1, 0, Math.PI * 2);
  ctx.restore();
  ctx.fill();
}

/**
 * Parlor silhouette — a Canvas2D-painted alpha mask of a Victorian
 * wingback chair + oil lamp. Used as the billboard texture on an
 * overlay quad that sits in front of the camera during the
 * parlor-dissolve preset. The texture renders warm-tinted on a
 * transparent background so only the silhouette reads.
 */
function makeParlorSilhouette(scene: Scene): Texture {
  const w = 256;
  const h = 256;
  const dt = new DynamicTexture("elderRevealParlorSilhouette", { width: w, height: h }, scene, false);
  const ctx = dt.getContext();
  ctx.clearRect(0, 0, w, h);

  // Warm-tinted wingback chair + oil lamp — brass ochre on plum wash.
  const fill = "rgba(204, 168, 118, 0.85)";
  ctx.fillStyle = fill;

  // Chair body (trapezoid / rounded top).
  ctx.beginPath();
  ctx.moveTo(72, 210);
  ctx.lineTo(72, 130);
  ctx.quadraticCurveTo(72, 82, 128, 82);
  ctx.quadraticCurveTo(184, 82, 184, 130);
  ctx.lineTo(184, 210);
  ctx.closePath();
  ctx.fill();

  // Chair back wing (a thin vertical flourish on the right side).
  ctx.beginPath();
  ctx.moveTo(180, 120);
  ctx.quadraticCurveTo(196, 90, 182, 64);
  ctx.quadraticCurveTo(172, 88, 172, 120);
  ctx.closePath();
  ctx.fill();

  // Seat cushion — a darker band at the base.
  ctx.fillStyle = "rgba(128, 96, 56, 0.9)";
  ctx.fillRect(74, 184, 108, 22);

  // Oil lamp on a side table — base + stem + flame bulb at center-left.
  // Use scaled arc() instead of ellipse() — the latter is absent from
  // Babylon's ICanvasRenderingContext typedef and some headless Canvas
  // polyfills.
  ctx.fillStyle = "rgba(232, 196, 138, 0.88)";
  // table
  ctx.fillRect(22, 180, 40, 30);
  // lamp base — wide shallow oval via transform-scaled arc.
  drawOval(ctx, 42, 176, 14, 6);
  // lamp stem
  ctx.fillRect(40, 148, 4, 28);
  // flame bulb
  drawOval(ctx, 42, 140, 10, 14);
  // flame core (hottest)
  ctx.fillStyle = "rgba(255, 236, 196, 0.95)";
  drawOval(ctx, 42, 140, 4, 7);

  dt.update(false);
  dt.hasAlpha = true;
  return dt;
}

export interface ElderRevealOptions {
  /** Canon preset to play. */
  preset: ElderRevealPreset;
  /** Total reveal duration in ms. Default 2500. */
  durationMs?: number;
  /** World position the reveal should center on (emitter anchor). Optional. */
  center?: Vector3;
  /** Fired once the reveal finishes + all resources are torn down. */
  onComplete?: () => void;
}

export class ElderRevealPostProcess {
  /** Current shader uniform strength [0..1]. Driven internally. */
  public strength = 0;

  /** True while the reveal is actively playing. */
  public isPlaying = false;

  private scene: Scene;
  private preset: ElderRevealPreset;
  private postProcess: PostProcess | null = null;
  private camera: Camera | null = null;

  // Shared particle textures (created lazily).
  private scrapSprite: Texture | null = null;
  private starSprite: Texture | null = null;
  private parlorSprite: Texture | null = null;

  // Active emitters / overlay mesh for cleanup.
  private scrapEmitter: ParticleSystem | null = null;
  private starEmitter: ParticleSystem | null = null;
  private parlorOverlay: Mesh | null = null;
  private parlorMaterial: StandardMaterial | null = null;

  // rAF handles so stop()/dispose() cancel in-flight animations.
  private rafHandles: number[] = [];
  private timerHandles: ReturnType<typeof setTimeout>[] = [];

  /**
   * Attach the post-process to the scene's active camera.
   *
   * Mirrors VisionPostProcess's NullEngine-resilience pattern — the
   * shader compilation can throw under test harnesses without a real
   * WebGL context, so we swallow the error and run the reveal with only
   * the particle layer active. `isAttached` surfaces the outcome.
   */
  constructor(scene: Scene, preset: ElderRevealPreset) {
    this.scene = scene;
    this.preset = preset;
    const cam = scene.activeCamera;
    if (!cam) {
      // No camera yet (scene still bootstrapping) — particles only.
      return;
    }
    this.camera = cam;
    try {
      this.postProcess = new PostProcess(
        "elderRevealPostProcess",
        "elderRevealPost",
        ["strength", "preset", "chromatic"],
        null,
        1.0,
        cam,
        Texture.BILINEAR_SAMPLINGMODE,
        scene.getEngine(),
        false,
      );
      this.postProcess.onApply = (effect) => {
        effect.setFloat("strength", this.strength);
        effect.setFloat("preset", this.preset === "parlor-dissolve" ? 1.0 : 0.0);
        effect.setFloat("chromatic", 0.012);
      };
    } catch (err) {
      // Headless / non-WebGL — fall back to particles-only playback.
      this.postProcess = null;
      console.warn("[ElderRevealPostProcess] shader unavailable:", err);
    }
  }

  /** True when the shader pass is live on a camera. */
  get isAttached(): boolean {
    return this.postProcess !== null;
  }

  /** Which preset was this instance built for. */
  get activePreset(): ElderRevealPreset {
    return this.preset;
  }

  /**
   * Run the full reveal: ramp up → hold → ramp down → onComplete.
   * The emitter + overlay quads are auto-disposed when their lifetimes
   * finish. Calling this while already playing is a no-op.
   */
  start(options: Omit<ElderRevealOptions, "preset">): void {
    if (this.isPlaying) return;
    this.isPlaying = true;

    const duration = options.durationMs ?? 2500;
    const center = options.center ?? this.cameraAnchor();

    // Split the duration: 28% ramp up → 44% hold → 28% ramp down.
    const rampUp = Math.round(duration * 0.28);
    const hold = Math.round(duration * 0.44);
    const rampDown = duration - rampUp - hold;

    if (this.preset === "starry-void") {
      this.startStarryVoidParticles(center, duration);
    } else {
      this.startParlorDissolveOverlay(center, duration);
    }

    this.animateStrength(1.0, rampUp, () => {
      const hid = setTimeout(() => {
        const idx = this.timerHandles.indexOf(hid);
        if (idx >= 0) this.timerHandles.splice(idx, 1);
        this.animateStrength(0, rampDown, () => {
          this.isPlaying = false;
          options.onComplete?.();
        });
      }, hold);
      this.timerHandles.push(hid);
    });
  }

  /**
   * Animate `strength` from its current value to `target` over
   * `durationMs`. Smoothstep easing keeps the ramp feeling like a
   * curtain pulling rather than a linear dissolve.
   */
  animateStrength(target: number, durationMs: number, onComplete?: () => void): void {
    const start = this.strength;
    const delta = target - start;
    if (Math.abs(delta) < 1e-4 || durationMs <= 0) {
      this.strength = target;
      onComplete?.();
      return;
    }
    const startTs = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - startTs) / durationMs);
      const eased = t * t * (3 - 2 * t);
      this.strength = start + delta * eased;
      if (t < 1) {
        const handle = requestAnimationFrame(step);
        this.rafHandles.push(handle);
      } else {
        this.strength = target;
        onComplete?.();
      }
    };
    const handle = requestAnimationFrame(step);
    this.rafHandles.push(handle);
  }

  /** Immediately snap strength back to zero and cancel pending ramps. */
  reset(): void {
    this.strength = 0;
    this.isPlaying = false;
    for (const h of this.rafHandles) {
      try { cancelAnimationFrame(h); } catch { /* ignore */ }
    }
    this.rafHandles.length = 0;
    for (const t of this.timerHandles) {
      clearTimeout(t);
    }
    this.timerHandles.length = 0;
  }

  /**
   * Tear down the post-process + all particle/overlay resources.
   * Idempotent — safe to call from stop paths and test teardown.
   */
  dispose(): void {
    this.reset();

    if (this.scrapEmitter) {
      try { this.scrapEmitter.dispose(); } catch { /* ignore */ }
      this.scrapEmitter = null;
    }
    if (this.starEmitter) {
      try { this.starEmitter.dispose(); } catch { /* ignore */ }
      this.starEmitter = null;
    }
    if (this.parlorOverlay) {
      try { this.parlorOverlay.dispose(); } catch { /* ignore */ }
      this.parlorOverlay = null;
    }
    if (this.parlorMaterial) {
      try { this.parlorMaterial.dispose(); } catch { /* ignore */ }
      this.parlorMaterial = null;
    }
    if (this.scrapSprite) {
      try { this.scrapSprite.dispose(); } catch { /* ignore */ }
      this.scrapSprite = null;
    }
    if (this.starSprite) {
      try { this.starSprite.dispose(); } catch { /* ignore */ }
      this.starSprite = null;
    }
    if (this.parlorSprite) {
      try { this.parlorSprite.dispose(); } catch { /* ignore */ }
      this.parlorSprite = null;
    }

    if (this.postProcess && this.camera) {
      try { this.camera.detachPostProcess(this.postProcess); } catch { /* ignore */ }
      try { this.postProcess.dispose(); } catch { /* ignore */ }
    }
    this.postProcess = null;
    this.camera = null;
  }

  // ---------- preset emitters ----------

  private cameraAnchor(): Vector3 {
    const cam = this.scene.activeCamera;
    if (cam) return cam.position.clone();
    return new Vector3(0, 2, 0);
  }

  /**
   * Starry-void emitter sequence: brown scrap rains from above, then
   * stars begin to spawn in the same region as the scrap fades out.
   * Both emitters auto-dispose when their particles finish.
   */
  private startStarryVoidParticles(center: Vector3, durationMs: number): void {
    this.scrapSprite = makeScrapSprite(this.scene);
    this.starSprite = makeStarSprite(this.scene);

    // Scrap curtain — falls from ~6 units above the center.
    const scrap = new ParticleSystem("elderRevealScrap", 220, this.scene);
    scrap.particleTexture = this.scrapSprite;
    scrap.emitter = new Vector3(center.x, center.y + 6, center.z);
    scrap.minEmitBox = new Vector3(-6, 0, -0.5);
    scrap.maxEmitBox = new Vector3(6, 0.4, 0.5);
    scrap.color1 = new Color4(0.65, 0.42, 0.2, 0.95);       // rust
    scrap.color2 = new Color4(0.35, 0.22, 0.12, 0.85);      // soot
    scrap.colorDead = new Color4(0.08, 0.05, 0.03, 0);
    scrap.minSize = 0.12;
    scrap.maxSize = 0.28;
    scrap.minLifeTime = 1.4;
    scrap.maxLifeTime = 2.2;
    scrap.emitRate = 180;
    scrap.minEmitPower = 0.2;
    scrap.maxEmitPower = 0.9;
    scrap.direction1 = new Vector3(-0.2, -2.4, 0);
    scrap.direction2 = new Vector3(0.2, -3.2, 0);
    scrap.gravity = new Vector3(0, -5.5, 0);
    scrap.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    // Stop emitting slightly before the hold ends so the curtain
    // visibly dissolves into the stars below.
    scrap.targetStopDuration = durationMs * 0.55 / 1000;
    scrap.disposeOnStop = true;
    scrap.start();
    this.scrapEmitter = scrap;

    // Star curtain — begins about 45% in, drifts gently, lives longer.
    const stars = new ParticleSystem("elderRevealStars", 140, this.scene);
    stars.particleTexture = this.starSprite;
    stars.emitter = new Vector3(center.x, center.y + 1.5, center.z);
    stars.minEmitBox = new Vector3(-7, -3, -1.2);
    stars.maxEmitBox = new Vector3(7, 4, 1.2);
    stars.color1 = new Color4(1.0, 1.0, 1.0, 0.95);
    stars.color2 = new Color4(0.6, 0.85, 1.0, 0.9);
    stars.colorDead = new Color4(0.1, 0.15, 0.3, 0);
    stars.minSize = 0.04;
    stars.maxSize = 0.14;
    stars.minLifeTime = 1.8;
    stars.maxLifeTime = 3.0;
    stars.emitRate = 0; // held at zero until the delayed start kicks in
    stars.minEmitPower = 0.05;
    stars.maxEmitPower = 0.3;
    stars.direction1 = new Vector3(-0.2, -0.05, -0.2);
    stars.direction2 = new Vector3(0.2, 0.2, 0.2);
    stars.blendMode = ParticleSystem.BLENDMODE_ADD;
    stars.targetStopDuration = durationMs * 0.7 / 1000;
    stars.disposeOnStop = true;
    stars.start();
    this.starEmitter = stars;

    // Kick the stars up after the scrap has started falling.
    const delay = Math.round(durationMs * 0.35);
    const timer = setTimeout(() => {
      const idx = this.timerHandles.indexOf(timer);
      if (idx >= 0) this.timerHandles.splice(idx, 1);
      // Guard against the emitter being disposed mid-delay (reset/
      // dispose races on skipStep). `isStarted()` is the public surface
      // that tracks whether the emitter is still running.
      if (this.starEmitter && this.starEmitter.isStarted()) {
        this.starEmitter.emitRate = 220;
      }
    }, delay);
    this.timerHandles.push(timer);
  }

  /**
   * Parlor-dissolve overlay: a billboard quad painted with the
   * wingback-chair silhouette hangs in front of the camera and
   * dissolves via its own alpha ramp as the shader's chromatic wash
   * swells. No particle curtain — the dissolve is a post-fx beat,
   * not a physical curtain like the starry-void scrap.
   */
  private startParlorDissolveOverlay(center: Vector3, durationMs: number): void {
    this.parlorSprite = makeParlorSilhouette(this.scene);

    // Place the overlay quad ~3 units in front of the camera along the
    // camera's forward. We use a plane billboard so it stays readable
    // even if the scene camera is at a slight angle.
    const cam = this.scene.activeCamera;
    const anchor = new Vector3(center.x, center.y, center.z);
    if (cam) {
      const forward = cam.getForwardRay().direction.normalizeToNew();
      anchor.copyFrom(cam.position).addInPlace(forward.scale(3.5));
    }

    const plane = MeshBuilder.CreatePlane(
      "elderRevealParlorOverlay",
      { width: 6, height: 6 },
      this.scene,
    );
    plane.position = anchor;
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    plane.isPickable = false;
    plane.renderingGroupId = 1;

    const mat = new StandardMaterial("elderRevealParlorOverlayMat", this.scene);
    mat.diffuseTexture = this.parlorSprite;
    // Make the wash plum so the silhouette reads on warm-wood parlor
    // ambience. Emissive carries the silhouette so scene lighting can't
    // wash it out, then we animate alpha from 0 → 0.9 → 0.
    mat.emissiveTexture = this.parlorSprite;
    mat.useAlphaFromDiffuseTexture = true;
    mat.disableLighting = true;
    mat.backFaceCulling = false;
    mat.alpha = 0;
    plane.material = mat;

    this.parlorOverlay = plane;
    this.parlorMaterial = mat;

    // Animate alpha on its own ramp so the silhouette fades in, holds,
    // and dissolves outward (matched to the shader chromatic swell).
    const fadeIn = Math.round(durationMs * 0.3);
    const hold = Math.round(durationMs * 0.35);
    const fadeOut = durationMs - fadeIn - hold;

    this.animateAlpha(mat, 0.9, fadeIn, () => {
      const t = setTimeout(() => {
        const idx = this.timerHandles.indexOf(t);
        if (idx >= 0) this.timerHandles.splice(idx, 1);
        this.animateAlpha(mat, 0, fadeOut, () => {
          // Dispose the quad + material as soon as the dissolve ends
          // so the scene graph is clean by the time dialogue starts.
          if (this.parlorOverlay) {
            try { this.parlorOverlay.dispose(); } catch { /* ignore */ }
            this.parlorOverlay = null;
          }
          if (this.parlorMaterial) {
            try { this.parlorMaterial.dispose(); } catch { /* ignore */ }
            this.parlorMaterial = null;
          }
        });
      }, hold);
      this.timerHandles.push(t);
    });
  }

  /**
   * Linear alpha ramp on the parlor overlay's material. Intentionally
   * separate from `animateStrength` (which drives the shader uniform)
   * — they share pacing but target different resources.
   */
  private animateAlpha(mat: StandardMaterial, target: number, durationMs: number, onComplete?: () => void): void {
    const start = mat.alpha;
    const delta = target - start;
    if (Math.abs(delta) < 1e-4 || durationMs <= 0) {
      mat.alpha = target;
      onComplete?.();
      return;
    }
    const startTs = performance.now();
    const step = (now: number): void => {
      if (!this.parlorMaterial) {
        // Material was disposed mid-animation (stop/dispose racey) —
        // bail without invoking the completion callback so the next
        // ramp doesn't trigger on a dead reference.
        return;
      }
      const t = Math.min(1, (now - startTs) / durationMs);
      const eased = t * t * (3 - 2 * t);
      mat.alpha = start + delta * eased;
      if (t < 1) {
        const handle = requestAnimationFrame(step);
        this.rafHandles.push(handle);
      } else {
        mat.alpha = target;
        onComplete?.();
      }
    };
    const handle = requestAnimationFrame(step);
    this.rafHandles.push(handle);
  }
}
